import React, { useEffect, useState, useRef } from 'react';
import { Row } from 'react-bootstrap';
import * as noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import CallKPISection from './CallKPISection';
import CallResultsTable from './CallResultsTable';
import HVASpinner from 'components/base/HVASpinner';
import { fetchHVACallResultsData } from 'services/hvareporting.service';

export interface CallData {
  customerref: string;
  accountref?: string;
  exp_id?: string;
  customerfullname?: string;
  serviceid?: number;
  servicename?: string;
  campaignid?: number;
  bxpcampaignid?: number;
  campaignname?: string;
  phonenumber?: string;
  calltype?: string;
  callid?: string;
  uniqueid?: string;
  linkedid?: string;
  calldatetime?: string;
  endcalldatetime?: string;
  phoneresult?: string;
  duration?: number;
  transferenciatpv?: number;
  transferenciagestor?: number;
  identificadaia?: number;
  rpc?: number;
  desglose_final?: string;
  resultado_final?: string;
  resultadopositivo?: number;
}

interface CallResultsDataProps {
  bxpcampaignid: number;
  startDate: Date;
  endDate: Date;
}

const CallResultsData: React.FC<CallResultsDataProps> = ({
  bxpcampaignid,
  startDate,
  endDate
}) => {
  const [callData, setCallData] = useState<CallData[]>([]);
  const [filteredData, setFilteredData] = useState<CallData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstanceRef = useRef<noUiSlider.API | null>(null);
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    startDate,
    endDate
  ]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchHVACallResultsData({
          bxpcampaignid: bxpcampaignid,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });

        if (Array.isArray(data) && data.length > 0) {
          setCallData(data);
          setFilteredData(data);
          setError(null);
        } else {
          throw new Error('No data found for the selected period');
        }
      } catch (error) {
        if (error instanceof Error) {
          setError('No call data found: ' + error.message);
        } else {
          setError('No call data found for the selected criteria.');
        }
        console.error('Error:', error);
        setCallData([]);
        setFilteredData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [bxpcampaignid, startDate, endDate]);

  // Configurar el slider para filtrar datos por fecha/hora
  useEffect(() => {
    if (
      sliderRef.current &&
      !sliderInstanceRef.current &&
      callData.length > 0
    ) {
      // Ordenar datos por fecha
      const sortedData = [...callData].sort((a, b) => {
        const dateA = a.calldatetime ? new Date(a.calldatetime).getTime() : 0;
        const dateB = b.calldatetime ? new Date(b.calldatetime).getTime() : 0;
        return dateA - dateB;
      });

      sliderInstanceRef.current = noUiSlider.create(sliderRef.current, {
        start: [0, sortedData.length - 1],
        connect: true,
        range: {
          min: 0,
          max: sortedData.length - 1
        },
        step: 1
      });

      sliderInstanceRef.current.on('update', (values: (string | number)[]) => {
        const [start, end] = values.map(value => Math.round(Number(value)));
        const startIdx = Math.min(start, sortedData.length - 1);
        const endIdx = Math.min(end, sortedData.length - 1);

        const filtered = sortedData.slice(startIdx, endIdx + 1);
        setFilteredData(filtered);

        const startData = sortedData[startIdx];
        const endData = sortedData[endIdx];

        if (startData?.calldatetime && endData?.calldatetime) {
          setDateRange([
            new Date(startData.calldatetime),
            new Date(endData.calldatetime)
          ]);
        }
      });
    }

    return () => {
      if (sliderInstanceRef.current) {
        sliderInstanceRef.current.destroy();
        sliderInstanceRef.current = null;
      }
    };
  }, [callData]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5 pb-7">
        <HVASpinner />
      </div>
    );
  }

  if (error) {
    return <div className="pt-4 pb-4 text-danger fs-9">{error}</div>;
  }

  return (
    <div className="">
      {/* Slider for date range filtering */}
      <Row className="gy-3 mt-0 mb-4 justify-content-between bg-body-emphasis">
        <div className="col-12">
          <h5>Call KPI's</h5>
          <div className="px-3">
            <div className="pt-2 mb-2 d-flex justify-content-between">
              <span className="fs-10">
                {dateRange[0]?.toLocaleString()} -{' '}
                {dateRange[1]?.toLocaleString()}
              </span>
              <span className="fs-10">
                {filteredData.length} calls selected
              </span>
            </div>
            <div ref={sliderRef} className="mt-2" style={{ height: '12px' }} />
          </div>
        </div>
      </Row>

      {/* Secciones de KPIs y gráficos */}
      <CallKPISection data={filteredData} />

      {/* Complete data table */}
      <Row className="gy-3 mt-0 justify-content-between bg-body-emphasis">
        <div className="col-12">
          <CallResultsTable data={callData} />
        </div>
      </Row>
    </div>
  );
};

export default CallResultsData;
